import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  partyApi,
  friendApi,
  type PartyData,
  type PartyMemberProfile,
  type FriendsList,
} from '../../auth/authApi';
import { useAuthStore } from '../../auth/authStore';
import { useTranslation } from '../../i18n/useTranslation';
import { useParty, type UsePartyResult } from '../../hooks/useParty';
import { useToast, ToastContainer } from '../../components/Toast/Toast';
import styles from './UserPage.module.css';

// ─────────────────────────────────────────────────────────────────────────────

const CLASS_ICON: Record<string, string> = {
  warrior: '⚔',  mage: '🔮', rogue: '🗡', archer: '🏹', cleric: '✨',
};
const CLASS_COLOR: Record<string, string> = {
  warrior: '#c87c30', mage: '#8060d0', rogue: '#40a060', archer: '#30a0c0', cleric: '#e0c040',
};

// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  token: string;
  /**
   * 'dashboard' (default): shows the "Start" button — battle launches
   * instantly for the whole party.
   * 'hub': the Hub Map has a physical Portal Guardian the whole party must
   * gather at instead (see HubMap.tsx's gather-detection effect) — the Start
   * button is replaced with a hint pointing there.
   */
  variant?: 'dashboard' | 'hub';
  /**
   * Pass HubMap's own useParty() result here to reuse its poll instead of
   * starting a second one (HubMap already needs it for the Portal Guardian
   * gather-detection). Omit for the standalone dashboard usage, where
   * PartySection polls on its own.
   */
  partyState?: UsePartyResult;
}

export default function PartySection({ token, variant = 'dashboard', partyState }: Props) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const myUserId = user?.id ?? '';
  const { t } = useTranslation();

  // ── Party + invite state — either our own poll, or one passed in (Hub) ────
  const ownPartyState = useParty(partyState ? undefined : token);
  const { party, invite, notices, loading, reload, consumeNotices, markNavigated } = partyState ?? ownPartyState;
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [acting,    setActing]    = useState(false);
  const [starting,  setStarting]  = useState(false);

  // ── Friends list (for invite picker) ─────────────────────────────────────
  const [friends,      setFriends]      = useState<FriendsList | null>(null);
  const [showPicker,   setShowPicker]   = useState(false);
  const [inviteActing, setInviteActing] = useState<Record<string, boolean>>({});

  // ── Notices (e.g. "X declined your invite") — surface once as a toast.
  // When `partyState` was injected (Hub usage), HubMap owns this instead —
  // it stays mounted regardless of whether this dock window is open, so the
  // leader sees the toast even with the window closed. Skip here to avoid
  // showing it twice on the same shared notices array.
  const { messages: toastMessages, toast, remove: removeToast } = useToast();
  useEffect(() => {
    // No Hub, o HubMap fica montado e mostra os avisos mesmo com o dock
    // fechado — aqui só mostramos no dashboard (com ou sem poll injetado).
    if (variant === 'hub') return;
    if (notices.length === 0) return;
    for (const n of notices) {
      if (n.type === 'invite_declined') {
        toast.info(t('party.notice.declined').replace('{name}', n.name));
      }
    }
    consumeNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notices, variant]);

  // Load friends when invite picker opens
  useEffect(() => {
    if (!showPicker) return;
    friendApi.list(token)
      .then(setFriends)
      .catch(() => {});
  }, [showPicker, token]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const withAction = async (fn: () => Promise<void>) => {
    setActionErr(null);
    setActing(true);
    try {
      await fn();
      await reload();
    } catch (e) {
      setActionErr((e as Error).message);
    } finally {
      setActing(false);
    }
  };

  const isLeader = party?.leader_id === myUserId;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section className={styles.partySection}>
      <div className={styles.partySectionHeader}>
        <h2 className={styles.sectionTitle}>⚔ {t('party.title')}</h2>
      </div>

      {/* Section-level error — shown regardless of party/invite state */}
      {actionErr && <p className={styles.error}>{actionErr}</p>}

      {loading ? (
        <div className={styles.skeleton} style={{ height: 64 }} />
      ) : (
        <>
          {/* ── Pending invite ─────────────────────────────────────────────── */}
          {!party && invite && (
            <div className={styles.partyInviteCard}>
              <div className={styles.partyInviteInfo}>
                <span className={styles.partyInviteLabel}>{t('party.inviteFrom')}</span>
                <span className={styles.partyInviteName}>
                  {invite.leader.character?.name ?? invite.leader.email}
                </span>
                <span className={styles.partyInviteSub}>
                  {invite.size} {invite.size === 1 ? t('party.member') : t('party.membersPlural')}
                  {invite.status === 'in_combat'
                    ? ` • ${t('party.inCombat')}`
                    : ` • ${t('party.waitingAccept')}`}
                </span>
              </div>
              {invite.status !== 'in_combat' && (
                <div className={styles.partyInviteActions}>
                  <button
                    className={styles.btnPartyAccept}
                    disabled={acting}
                    onClick={() => withAction(() => partyApi.accept(token).then(() => {}))}
                  >{t('party.accept')}</button>
                  <button
                    className={styles.btnPartyDecline}
                    disabled={acting}
                    onClick={() => withAction(() => partyApi.decline(token).then(() => {}))}
                  >{t('party.decline')}</button>
                </div>
              )}
            </div>
          )}

          {/* ── No party, no invite ─────────────────────────────────────────── */}
          {!party && !invite && (
            <div className={styles.partyEmpty}>
              <p className={styles.partyEmptyText}>
                {t('party.empty')}
              </p>
              <button
                className={styles.btnCreateParty}
                disabled={acting}
                onClick={() => withAction(() => partyApi.create(token).then(() => {}))}
              >
                + {t('party.create')}
              </button>
            </div>
          )}

          {/* ── Active party ───────────────────────────────────────────────── */}
          {party && (
            <div className={styles.partyCard}>
              {/* Header */}
              <div className={styles.partyCardHeader}>
                <span className={styles.partyCardTitle}>
                  {t('party.title')} · {party.size}/{4}
                </span>
                <div className={styles.partyOnlineIndicator}>
                  {party.all_online
                    ? <span className={styles.allOnline}>● {t('party.allOnline')}</span>
                    : <span className={styles.notAllOnline}>● {t('party.waiting')}</span>
                  }
                </div>
              </div>

              {/* Members */}
              <div className={styles.partyMembers}>
                {/* Leader */}
                <MemberRow
                  profile={party.leader}
                  role="leader"
                  isMe={party.leader_id === myUserId}
                  canKick={false}
                  onKick={() => {}}
                />
                {/* Accepted members */}
                {party.members.map(m => (
                  <MemberRow
                    key={m.user_id}
                    profile={m}
                    role="member"
                    isMe={m.user_id === myUserId}
                    canKick={isLeader}
                    onKick={() => withAction(() => partyApi.kick(token, m.user_id).then(() => {}))}
                  />
                ))}
                {/* Pending invites */}
                {party.pending_invites.map(inv => (
                  <div key={inv.user_id} className={styles.partyMemberRow} data-pending="true">
                    <span className={styles.memberAvatar} style={{ opacity: 0.5 }}>
                      {inv.character ? (CLASS_ICON[inv.character.class] ?? '👤') : '👤'}
                    </span>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName} style={{ opacity: 0.6 }}>
                        {inv.character?.name ?? inv.email}
                      </span>
                      <span className={styles.memberStatus}>{t('party.pending')}</span>
                    </div>
                    {isLeader && (
                      <button
                        className={styles.btnMemberKick}
                        title={t('party.cancelInvite')}
                        onClick={() => withAction(() => partyApi.kick(token, inv.user_id).then(() => {}))}
                      >✕</button>
                    )}
                  </div>
                ))}

                {/* Invite new member slot */}
                {isLeader && party.size < 4 && party.status === 'waiting' && (
                  <button
                    className={styles.btnInviteSlot}
                    onClick={() => setShowPicker(v => !v)}
                  >
                    {showPicker ? `✕ ${t('party.cancelSlot')}` : `+ ${t('party.inviteSlot').replace('{x}', String(4 - party.size))}`}
                  </button>
                )}
              </div>

              {/* Friend picker */}
              {showPicker && friends && (
                <FriendPicker
                  friends={friends}
                  party={party}
                  acting={inviteActing}
                  onInvite={async (uid) => {
                    setInviteActing(prev => ({ ...prev, [uid]: true }));
                    try {
                      await partyApi.invite(token, uid);
                      await reload();
                    } catch (e) {
                      setActionErr((e as Error).message);
                    } finally {
                      setInviteActing(prev => ({ ...prev, [uid]: false }));
                    }
                  }}
                />
              )}

              {/* Start / Leave */}
              <div className={styles.partyActions}>
                {variant === 'hub' ? (
                  party.size > 1 && party.status === 'waiting' && (
                    <p className={styles.partyPortalHint}>🌀 {t('party.portal.hint')}</p>
                  )
                ) : isLeader ? (
                  <button
                    className={styles.btnStartParty}
                    disabled={starting || !party.all_online || party.size < 1 || party.status !== 'waiting'}
                    title={!party.all_online ? t('party.mustBeOnline') : ''}
                    onClick={async () => {
                      setStarting(true);
                      setActionErr(null);
                      try {
                        const res = await partyApi.start(token);
                        const myEntityId = res.entity_map[myUserId];
                        // Mark this combat as navigated so the hook's poll doesn't re-trigger it
                        markNavigated(res.combat_id);
                        navigate(`/play?combat_id=${res.combat_id}&entity_id=${myEntityId}`);
                      } catch (e) {
                        setActionErr((e as Error).message);
                        setStarting(false);
                      }
                    }}
                  >
                    {starting ? (
                      <><span className={styles.battleSpinner} /> {t('party.preparing')}</>
                    ) : (
                      `⚔  ${t('party.start')}`
                    )}
                  </button>
                ) : null}

                <button
                  className={styles.btnLeaveParty}
                  disabled={acting}
                  onClick={() => withAction(() => partyApi.leave(token).then(() => {}))}
                >
                  {isLeader ? t('party.disband') : t('party.leave')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {variant !== 'hub' && <ToastContainer messages={toastMessages} onRemove={removeToast} />}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

export function MemberRow({
  profile, role, isMe, canKick, onKick,
}: {
  profile: PartyMemberProfile;
  role: 'leader' | 'member';
  isMe: boolean;
  canKick: boolean;
  onKick: () => void;
}) {
  const { t } = useTranslation();
  const cls   = profile.character?.class ?? '';
  const icon  = CLASS_ICON[cls] ?? '👤';
  const color = CLASS_COLOR[cls] ?? '#888';

  return (
    <div
      className={styles.partyMemberRow}
      data-me={isMe ? 'true' : 'false'}
    >
      <span className={styles.memberAvatar} style={{ color }}>
        {icon}
      </span>
      <div className={styles.memberInfo}>
        <span className={styles.memberName}>
          {profile.character?.name ?? profile.email}
          {isMe && <span className={styles.memberYou}> {t('party.you')}</span>}
        </span>
        <span className={styles.memberStatus}>
          {role === 'leader' && <span className={styles.leaderBadge}>👑 {t('party.leaderBadge')}</span>}
          <span
            className={profile.is_online ? styles.onlineDot : styles.offlineDot}
          >
            {profile.is_online ? `● ${t('party.online')}` : `● ${t('party.offline')}`}
          </span>
        </span>
      </div>
      {canKick && (
        <button
          className={styles.btnMemberKick}
          title={t('party.kick')}
          onClick={onKick}
        >✕</button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function FriendPicker({
  friends, party, acting, onInvite,
}: {
  friends: FriendsList;
  party: PartyData;
  acting: Record<string, boolean>;
  onInvite: (userId: string) => void;
}) {
  const { t } = useTranslation();
  // Filter out people already in the party or with pending invites
  const alreadyInvolved = new Set([
    ...party.all_members,
    ...party.pending_invites.map(i => i.user_id),
  ]);

  const eligible = friends.friends.filter(f => !alreadyInvolved.has(f.user_id));

  if (eligible.length === 0) {
    return (
      <div className={styles.pickerEmpty}>
        {t('party.allFriendsIn')}
      </div>
    );
  }

  return (
    <div className={styles.friendPicker}>
      {eligible.map(f => {
        const cls  = f.character?.class ?? '';
        const icon = CLASS_ICON[cls] ?? '👤';
        return (
          <div key={f.user_id} className={styles.pickerRow}>
            <span className={styles.pickerAvatar}>{icon}</span>
            <span className={styles.pickerName}>
              {f.character?.name ?? f.email}
            </span>
            <button
              className={styles.btnPickerInvite}
              disabled={!!acting[f.user_id]}
              onClick={() => onInvite(f.user_id)}
            >
              {acting[f.user_id] ? '…' : t('party.invite')}
            </button>
          </div>
        );
      })}
    </div>
  );
}
